import type {
  ApiError,
  ArtistSearchAdminItem,
  CachePurgeScope,
  BlogPostAdminItem,
  BlogPostAdminListItem,
  CollectionAdminItem,
  CreateBlogPostInput,
  CreateCollectionInput,
  ImageRef,
  PaginatedResponse,
  UpdateBlogPostInput,
  CreateSearchChipInput,
  EventFilterOption,
  FeaturedArtistsAdminItem,
  FeaturedCitiesAdminItem,
  HomeLayoutAdminItem,
  ReorderSearchChipsInput,
  SearchChipAdminItem,
  SearchChipsAdminListResponse,
  UpdateCollectionInput,
  UpdateFeaturedArtistsInput,
  UpdateFeaturedCitiesInput,
  UpdateHomeLayoutInput,
  UpdateSearchChipInput,
} from '@bailafm/shared';
import { stytch } from '@/lib/stytch';

const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

/**
 * authorizedFetch — wrapper sobre fetch que añade el JWT de Stytch al
 * header `Authorization`. Si la API responde 401 limpia la sesión
 * local; el `ProtectedRoute` se encargará de redirigir a /login en
 * el siguiente render.
 */
export async function authorizedFetch(input: string, init: RequestInit = {}) {
  const tokens = stytch.session.getTokens();
  const jwt = tokens?.session_jwt;
  const headers = new Headers(init.headers);
  if (jwt) headers.set('Authorization', `Bearer ${jwt}`);
  // FormData fija su propio Content-Type (con boundary); no lo pisamos.
  if (
    init.body &&
    !(init.body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }

  const url = input.startsWith('http') ? input : `${baseUrl}${input}`;
  const res = await fetch(url, { ...init, headers });

  if (res.status === 401) {
    await stytch.session.revoke().catch(() => {});
  }

  return res;
}

/**
 * Lanza un Error enriquecido cuando la API devuelve un shape ApiError.
 * El mensaje legible y el `code` quedan disponibles para que la UI
 * decida cómo mostrarlo.
 */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(payload: ApiError) {
    super(payload.message);
    this.name = 'ApiClientError';
    this.status = payload.status;
    this.code = payload.code;
    this.details = payload.details;
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  if (res.ok) {
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }
  // Intentamos leer el shape canónico; si falla, fabricamos uno.
  let payload: ApiError;
  try {
    payload = (await res.json()) as ApiError;
  } catch {
    payload = {
      status: res.status,
      code: 'unknown_error',
      message: res.statusText || 'Request failed',
    };
  }
  throw new ApiClientError(payload);
}

/* ------------------------------------------------------------------ */
/*                          Search chips                              */
/* ------------------------------------------------------------------ */

export const chipsApi = {
  async list(): Promise<SearchChipAdminItem[]> {
    const res = await authorizedFetch('/admin/search-chips');
    const data = await unwrap<SearchChipsAdminListResponse>(res);
    return data.items;
  },

  async create(input: CreateSearchChipInput): Promise<SearchChipAdminItem> {
    const res = await authorizedFetch('/admin/search-chips', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return unwrap<SearchChipAdminItem>(res);
  },

  // Last-write-wins (sin If-Match): el reorder bumpea el _etag fuera del
  // formulario y exigir ETag aquí generaba 412/409 espurios. Mismo patrón
  // que blog y collections.
  async update(
    id: string,
    input: UpdateSearchChipInput,
  ): Promise<SearchChipAdminItem> {
    const res = await authorizedFetch(`/admin/search-chips/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return unwrap<SearchChipAdminItem>(res);
  },

  async delete(id: string, etag: string): Promise<void> {
    const res = await authorizedFetch(`/admin/search-chips/${id}`, {
      method: 'DELETE',
      headers: { 'If-Match': etag },
    });
    await unwrap<void>(res);
  },

  async reorder(input: ReorderSearchChipsInput): Promise<SearchChipAdminItem[]> {
    const res = await authorizedFetch('/admin/search-chips/reorder', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    const data = await unwrap<SearchChipsAdminListResponse>(res);
    return data.items;
  },
};

/* ------------------------------------------------------------------ */
/*                            Collections                             */
/* ------------------------------------------------------------------ */

export const collectionsApi = {
  // Listado completo (para selectores y usos que requieren todas).
  async listAll(): Promise<CollectionAdminItem[]> {
    const res = await authorizedFetch('/admin/collections/all');
    const data = await unwrap<{ items: CollectionAdminItem[] }>(res);
    return data.items;
  },

  // Listado paginado (para las tablas del admin).
  async list(
    page: number = 1,
    pageSize: number = 20,
  ): Promise<PaginatedResponse<CollectionAdminItem>> {
    const res = await authorizedFetch(
      `/admin/collections?page=${page}&pageSize=${pageSize}`,
    );
    return unwrap<PaginatedResponse<CollectionAdminItem>>(res);
  },

  async create(input: CreateCollectionInput): Promise<CollectionAdminItem> {
    const res = await authorizedFetch('/admin/collections', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return unwrap<CollectionAdminItem>(res);
  },

  // Last-write-wins: sin If-Match (editor de un solo usuario; ver nota en
  // el controller de la API).
  async update(id: string, input: UpdateCollectionInput): Promise<CollectionAdminItem> {
    const res = await authorizedFetch(`/admin/collections/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return unwrap<CollectionAdminItem>(res);
  },

  async delete(id: string, etag: string): Promise<void> {
    const res = await authorizedFetch(`/admin/collections/${id}`, {
      method: 'DELETE',
      headers: { 'If-Match': etag },
    });
    await unwrap<void>(res);
  },

  // Sube una imagen (page|thumbnail) recortada. Envía el original +
  // la región de recorte; la API recorta/optimiza con sharp y la sube
  // a Azure Blob. Devuelve el ImageRef ya persistido en el doc.
  async uploadImage(
    id: string,
    target: 'page' | 'thumbnail',
    file: File | Blob,
    crop: { x: number; y: number; width: number; height: number },
  ): Promise<ImageRef> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('cropX', String(Math.round(crop.x)));
    fd.append('cropY', String(Math.round(crop.y)));
    fd.append('cropWidth', String(Math.round(crop.width)));
    fd.append('cropHeight', String(Math.round(crop.height)));
    const res = await authorizedFetch(`/admin/collections/${id}/images/${target}`, {
      method: 'POST',
      body: fd,
    });
    return unwrap<ImageRef>(res);
  },
};

/* ------------------------------------------------------------------ */
/*                               Blog                                 */
/* ------------------------------------------------------------------ */

export type BlogImageTarget = 'featured' | 'thumbnail' | 'content';

export const blogApi = {
  async list(
    page: number = 1,
    pageSize: number = 20,
  ): Promise<PaginatedResponse<BlogPostAdminListItem>> {
    const res = await authorizedFetch(
      `/admin/blog-posts?page=${page}&pageSize=${pageSize}`,
    );
    return unwrap<PaginatedResponse<BlogPostAdminListItem>>(res);
  },

  async get(id: string): Promise<BlogPostAdminItem> {
    const res = await authorizedFetch(`/admin/blog-posts/${id}`);
    return unwrap<BlogPostAdminItem>(res);
  },

  async create(input: CreateBlogPostInput): Promise<BlogPostAdminItem> {
    const res = await authorizedFetch('/admin/blog-posts', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return unwrap<BlogPostAdminItem>(res);
  },

  // Last-write-wins: el blog no usa If-Match (editor de un solo usuario;
  // ver nota en el controller de la API).
  async update(id: string, input: UpdateBlogPostInput): Promise<BlogPostAdminItem> {
    const res = await authorizedFetch(`/admin/blog-posts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
    return unwrap<BlogPostAdminItem>(res);
  },

  async delete(id: string, etag: string): Promise<void> {
    const res = await authorizedFetch(`/admin/blog-posts/${id}`, {
      method: 'DELETE',
      headers: { 'If-Match': etag },
    });
    await unwrap<void>(res);
  },

  // Sube una imagen recortada. featured/thumbnail se persisten en el doc
  // (featured deriva og); content devuelve el ImageRef para colocarlo en
  // un bloque de imagen del cuerpo.
  async uploadImage(
    id: string,
    target: BlogImageTarget,
    file: File | Blob,
    crop: { x: number; y: number; width: number; height: number },
  ): Promise<ImageRef> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('cropX', String(Math.round(crop.x)));
    fd.append('cropY', String(Math.round(crop.y)));
    fd.append('cropWidth', String(Math.round(crop.width)));
    fd.append('cropHeight', String(Math.round(crop.height)));
    const res = await authorizedFetch(`/admin/blog-posts/${id}/images/${target}`, {
      method: 'POST',
      body: fd,
    });
    return unwrap<ImageRef>(res);
  },

  // Mintea un token corto para abrir la previsualización del borrador.
  async previewToken(id: string): Promise<string> {
    const res = await authorizedFetch(`/admin/blog-posts/${id}/preview-token`, {
      method: 'POST',
    });
    const data = await unwrap<{ token: string }>(res);
    return data.token;
  },
};

/* ------------------------------------------------------------------ */
/*                          Events (search)                           */
/* ------------------------------------------------------------------ */

// Mismo shape que EventsSearchAdminResponse en apps/api. No vive en
// @bailafm/shared porque hoy solo lo consume el admin; si más
// adelante hace falta en la web pública se promociona.
export interface AdminEventSearchItem {
  id: string;
  slug?: string;
  title: string;
  imageUrl?: string;
  startAt?: string;
  endAt?: string;
  city?: string;
  venue?: string;
  minPrice?: number;
}

export interface AdminEventsSearchResponse {
  items: AdminEventSearchItem[];
  page: number;
  pageSize: number;
  total: number;
  hasNextPage: boolean;
}

export const eventsApi = {
  async search(params: {
    q?: string;
    page?: number;
    pageSize?: number;
  }): Promise<AdminEventsSearchResponse> {
    const url = new URL('/admin/events/search', 'http://placeholder');
    if (params.q) url.searchParams.set('q', params.q);
    if (params.page) url.searchParams.set('page', String(params.page));
    if (params.pageSize) url.searchParams.set('pageSize', String(params.pageSize));
    const res = await authorizedFetch(`${url.pathname}?${url.searchParams}`);
    return unwrap<AdminEventsSearchResponse>(res);
  },

  // Hidrata el detalle de varios eventos por id. Sirve al modo edición
  // del formulario de colecciones para poblar la lista de seleccionados.
  async byIds(ids: readonly string[]): Promise<AdminEventSearchItem[]> {
    if (ids.length === 0) return [];
    const url = new URL('/admin/events/by-ids', 'http://placeholder');
    url.searchParams.set('ids', ids.join(','));
    const res = await authorizedFetch(`${url.pathname}?${url.searchParams}`);
    const data = await unwrap<{ items: AdminEventSearchItem[] }>(res);
    return data.items;
  },

  // Resuelve un evento por su slug para hidratar el bloque de evento del
  // editor de blog al abrir un post ya guardado (el bloque solo persiste
  // el slug). Devuelve null si el Core ya no tiene ese evento.
  async bySlug(slug: string): Promise<AdminEventSearchItem | null> {
    if (!slug) return null;
    const url = new URL('/admin/events/by-slug', 'http://placeholder');
    url.searchParams.set('slug', slug);
    const res = await authorizedFetch(`${url.pathname}?${url.searchParams}`);
    const data = await unwrap<{ item: AdminEventSearchItem | null }>(res);
    return data.item;
  },
};

/* ------------------------------------------------------------------ */
/*                            Home layout                             */
/* ------------------------------------------------------------------ */

export const homeLayoutApi = {
  async get(): Promise<HomeLayoutAdminItem> {
    const res = await authorizedFetch('/admin/home-layout');
    return unwrap<HomeLayoutAdminItem>(res);
  },

  async update(
    etag: string | undefined,
    input: UpdateHomeLayoutInput,
  ): Promise<HomeLayoutAdminItem> {
    const headers: Record<string, string> = {};
    if (etag) headers['If-Match'] = etag;
    const res = await authorizedFetch('/admin/home-layout', {
      method: 'PUT',
      headers,
      body: JSON.stringify(input),
    });
    return unwrap<HomeLayoutAdminItem>(res);
  },
};

/* ------------------------------------------------------------------ */
/*                       Featured artists                             */
/* ------------------------------------------------------------------ */

export const featuredArtistsApi = {
  async get(): Promise<FeaturedArtistsAdminItem> {
    const res = await authorizedFetch('/admin/featured-artists');
    return unwrap<FeaturedArtistsAdminItem>(res);
  },

  async update(
    etag: string | undefined,
    input: UpdateFeaturedArtistsInput,
  ): Promise<FeaturedArtistsAdminItem> {
    const headers: Record<string, string> = {};
    if (etag) headers['If-Match'] = etag;
    const res = await authorizedFetch('/admin/featured-artists', {
      method: 'PUT',
      headers,
      body: JSON.stringify(input),
    });
    return unwrap<FeaturedArtistsAdminItem>(res);
  },
};

// Buscador de artistas del Core (B2C autenticado). Alimenta el form de
// artistas destacados. Devuelve el array directamente (no envuelto).
export const artistsApi = {
  async search(q: string): Promise<ArtistSearchAdminItem[]> {
    const url = new URL('/admin/artists/search', 'http://placeholder');
    if (q) url.searchParams.set('q', q);
    const res = await authorizedFetch(`${url.pathname}?${url.searchParams}`);
    return unwrap<ArtistSearchAdminItem[]>(res);
  },
};

/* ------------------------------------------------------------------ */
/*                        Featured cities                             */
/* ------------------------------------------------------------------ */

export const featuredCitiesApi = {
  async get(): Promise<FeaturedCitiesAdminItem> {
    const res = await authorizedFetch('/admin/featured-cities');
    return unwrap<FeaturedCitiesAdminItem>(res);
  },

  async update(
    etag: string | undefined,
    input: UpdateFeaturedCitiesInput,
  ): Promise<FeaturedCitiesAdminItem> {
    const headers: Record<string, string> = {};
    if (etag) headers['If-Match'] = etag;
    const res = await authorizedFetch('/admin/featured-cities', {
      method: 'PUT',
      headers,
      body: JSON.stringify(input),
    });
    return unwrap<FeaturedCitiesAdminItem>(res);
  },
};

// Faceta de ciudades del store (las que tienen eventos). Alimenta el
// selector de ciudades destacadas. Devuelve el array directamente.
export const citiesApi = {
  async list(): Promise<EventFilterOption[]> {
    const res = await authorizedFetch('/admin/cities');
    return unwrap<EventFilterOption[]>(res);
  },
};

/* ------------------------------------------------------------------ */
/*                       Purga manual de caché                        */
/* ------------------------------------------------------------------ */

// Fuerza la invalidación de caché de la web (página Configuración).
// `scope` = 'all' (todo) o una superficie concreta.
export const cacheApi = {
  async purge(scope: CachePurgeScope): Promise<{ purged: string[] }> {
    const res = await authorizedFetch('/admin/cache/purge', {
      method: 'POST',
      body: JSON.stringify({ scope }),
    });
    return unwrap<{ purged: string[] }>(res);
  },
};
